/**
 * The shape of one Marvin turn as the conversation draws it (design handoff §4–§6): the
 * steps Marvin took, which one is running, and what the turn's header says. Pure: built
 * from the message parts, so live and reloaded turns read the same.
 */
import { helperIdOf } from "../../hooks/streamReducer";
import {
    parseApiMessagePart,
    type ActivityEntityRef,
    type ActivityPartPayload,
    type ActivityPlanGroup,
    type ApiMessagePart,
} from "../messageParts";
import {
    lookupItem,
    parsePlan,
    shortNoun,
    type PlanGroupStatus,
    type PlanModel,
} from "./planModel";

/**
 * What Marvin is doing, as one of five working operations and two end states (design
 * handoff §5). `done` is not an operation: a finished step shows the static step check.
 */
export type MarvinOp = "think" | "look" | "check" | "write" | "wait" | "stopped" | "error";

/** Operations a step can have (waiting and the end states belong to the turn, not a tool). */
export type StepOp = Extract<MarvinOp, "think" | "look" | "check" | "write">;

/** Where the turn is. `live` is a turn still streaming. */
export type TurnPhase = "live" | "complete" | "cancelled" | "error";

/**
 * A step's state. `stopped` and `unfinished` are the step that was running when the user
 * stopped Marvin or the connection dropped; `failed` is a step whose tool returned an error.
 */
export type StepStatus = "done" | "running" | "failed" | "stopped" | "unfinished";

/**
 * A reference a step's label names: the words that name it there, and what opens it (a
 * citation of the turn by its keys, or an entity by its UUID).
 */
export interface StepRef {
    text: string;
    referenceKeys?: string[];
    ref?: ActivityEntityRef;
}

export interface TurnStep {
    id: string;
    op: StepOp;
    /** The step in the present tense, references resolved ("Searching drawings for …"). */
    label: string;
    status: StepStatus;
    /**
     * `narration`: a sentence Marvin said before a group of steps (his `narrate` tool). It reads
     * as commentary, not as a step: never past-tensed, counted, or named by the header.
     */
    kind?: "narration" | "plan";
    /** The plan a `plan` step draws: its snapshot, updated in place as groups start and finish. */
    plan?: PlanModel;
    resultCount?: number;
    resultNoun?: string;
    /** Why a failed step failed, in its tool's words. */
    error?: string;
    /** The references the label names, in the words it names them with. */
    refs?: StepRef[];
    /**
     * The helper that did this step ("MiniMarv"): all of one helper's lookups are this one step,
     * counted as what it found, not listed lookup by lookup.
     */
    helper?: string;
    /** What the helper was asked to do, in one short sentence (S1b's row says it in words). */
    task?: string;
}

/** One lookup a plan group's MiniMarv made: its label, and the plan item it names when it quotes one. */
export interface LaneLookup {
    label: string;
    item: string | null;
}

/** A MiniMarv reading one plan group: the lane the plan block's group opens into (board S2). */
export interface TurnLane {
    id: string;
    helperId: string;
    name: string;
    task?: string;
    planId: string;
    group: number;
    /** The group's range and size, as the helper's parts tag them. */
    label: string;
    size: number;
    worker?: number;
    status: PlanGroupStatus;
    /** The helper's last two lookups, oldest first. */
    lookups: LaneLookup[];
    /** Every lookup the helper made, in order: what its row's tooltip lists. */
    opened: LaneLookup[];
}

export interface TurnModel {
    steps: TurnStep[];
    /** The Deep research plan this turn drew, when it ran one. */
    plan: PlanModel | null;
    /** The MiniMarvs reading the plan's groups, by group number. */
    lanes: TurnLane[];
    /** The step running now (live turns only). */
    current: TurnStep | null;
    /** Some answer text has arrived. */
    hasText: boolean;
    /** The answer text is what is streaming now. */
    writing: boolean;
    /** A question card is waiting for the user's answer. */
    waiting: boolean;
}

/** The tool Marvin narrates with; its label is the sentence itself. */
const NARRATE_TOOL = "narrate";

/** A step that is work, not narration: what counts, windows and names the header. */
export function isCountedStep(step: TurnStep): boolean {
    return step.kind !== "narration" && step.kind !== "plan";
}

const STEP_OPS: ReadonlySet<string> = new Set(["think", "look", "check", "write"]);

const LOOK_TOOLS = new Set([
    "view_image",
    "view_sheet",
    "view_drawing_detail",
    "find_references",
    "view_tags",
    "search_tags",
    "search_text",
    "view_document",
    "view_comparison",
    "open_reference",
]);
const CHECK_TOOLS = new Set([
    "search_schedule",
    "search_project",
    "read_more",
    "check_review",
    "rfi_check",
    "rfi_extract",
]);
const WRITE_TOOLS = new Set([
    "present_artifact",
    "write_file",
    "edit_file",
    "rfi_draft_create",
    "rfi_draft_update",
    "submittal_package_draft",
    "issue_draft_create",
    "punch_draft_create",
    "submittal_draft_create",
]);

/**
 * A tool's operation when the server did not say (older servers send no `op`). The label
 * breaks the ties the tool name leaves open: a drawing search looks, any other search checks.
 */
export function opForTool(toolName: string | undefined, label: string): StepOp {
    const name = toolName ?? "";
    if (LOOK_TOOLS.has(name)) return "look";
    if (CHECK_TOOLS.has(name)) return "check";
    if (WRITE_TOOLS.has(name)) return "write";
    if (
        name === "search_documents" ||
        name === "search_document" ||
        name === "find" ||
        name === "open"
    ) {
        return /\b(drawings?|sheets?|details?|revisions?)\b/i.test(label) ? "look" : "check";
    }
    if (name === "read_file")
        return /\.(pdf|png|jpe?g|webp|tiff?)\b/i.test(label) ? "look" : "check";
    return "think";
}

// Present participle -> past tense for the labels the server builds ("Searching" -> "Searched").
const PAST: Record<string, string> = {
    Searching: "Searched",
    Reading: "Read",
    Viewing: "Viewed",
    Zooming: "Zoomed",
    Listing: "Listed",
    Opening: "Opened",
    Reviewing: "Reviewed",
    Finding: "Found",
    Inspecting: "Inspected",
    Counting: "Counted",
    Comparing: "Compared",
    Checking: "Checked",
    Running: "Ran",
    Researching: "Researched",
    Preparing: "Prepared",
    Writing: "Wrote",
    Looking: "Looked",
    Updating: "Updated",
    Planning: "Planned",
    Building: "Built",
    Following: "Followed",
    Marking: "Marked",
    Working: "Worked",
};

/** "Searching drawings · …" -> "Searched drawings · …"; a label with another verb stays. */
export function pastTense(label: string): string {
    const match = /^(\w+)(.*)$/s.exec(label);
    if (!match) return label;
    const past = PAST[match[1]];
    return past ? `${past}${match[2]}` : label;
}

/**
 * The running step in its own words for the turn's header: the label up to its quoted query
 * ("Searching contracts · "walkway…"" -> "Searching contracts…"); the step row below
 * carries the query.
 */
export function statusText(label: string): string {
    const quote = label.search(/["“]/);
    const words = (quote > 0 ? label.slice(0, quote) : label)
        .replace(/\s*·\s*$/, "")
        .replace(/\s+(for|matching|named|about|in|on|with|titled)\s*$/i, "")
        .replace(/(\.\.\.|…)\s*$/, "")
        .trim();
    return `${words || label.trim()}…`;
}

/** The label with its first letter lower-cased, for "Stopped while reading …". */
export function continuing(label: string): string {
    return label ? label.charAt(0).toLowerCase() + label.slice(1) : label;
}

/** "5 sheets", "1 RFI": the result a finished step found. */
export function resultText(count: number | undefined, noun: string | undefined): string | null {
    if (count === undefined || !noun) return null;
    if (count === 0) return `no ${noun}`;
    const word = count === 1 ? noun.replace(/ies$/, "y").replace(/s$/, "") : noun;
    return `${count} ${word}`;
}

const ENTITY_KINDS: ReadonlySet<string> = new Set(["rfi", "sheet", "spec", "page"]);
const CITATION_TOKEN_RE = /\{(\d+(?:,\s*\d+)*)\}/g;

function isEntityRef(value: unknown): value is ActivityEntityRef {
    if (typeof value !== "object" || value === null) return false;
    const ref = value as Record<string, unknown>;
    return (
        typeof ref.kind === "string" &&
        ENTITY_KINDS.has(ref.kind) &&
        typeof ref.code === "string" &&
        typeof ref.id === "string" &&
        ref.id.length > 0
    );
}

/** The references the server says a step's label names (`labelRefs`); malformed ones name nothing. */
function serverRefs(value: unknown): StepRef[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((item): StepRef[] => {
        if (typeof item !== "object" || item === null) return [];
        const { text, referenceKey, ref } = item as Record<string, unknown>;
        if (typeof text !== "string" || !text) return [];
        if (typeof referenceKey === "string" && referenceKey) {
            return [{ text, referenceKeys: [referenceKey] }];
        }
        return isEntityRef(ref) ? [{ text, ref }] : [];
    });
}

/** The citations a raw label names as `{12}` tokens, in the words `resolve` gives them. */
function tokenRefs(raw: string, resolve: (text: string) => string): StepRef[] {
    return [...raw.matchAll(CITATION_TOKEN_RE)].flatMap((match): StepRef[] => {
        const text = resolve(match[0]);
        return text && text !== match[0]
            ? [{ text, referenceKeys: match[1].split(/\s*,\s*/) }]
            : [];
    });
}

/**
 * A step's words split around the references they name, in order: text runs, and each
 * reference where its words stand. A reference whose words the text does not show is left out.
 */
export function labelParts(text: string, refs: readonly StepRef[] = []): Array<string | StepRef> {
    const shown = refs
        .filter((ref) => ref.text && text.includes(ref.text))
        .sort((a, b) => text.indexOf(a.text) - text.indexOf(b.text));
    const parts: Array<string | StepRef> = [];
    let from = 0;
    for (const ref of shown) {
        const at = text.indexOf(ref.text, from);
        if (at < 0) continue;
        if (at > from) parts.push(text.slice(from, at));
        parts.push(ref);
        from = at + ref.text.length;
    }
    if (from < text.length) parts.push(text.slice(from));
    return parts;
}

/** Parts that are drawn in the thread; a later one of these means the step before it ended. */
function isFlowPart(kind: string): boolean {
    return kind !== "reference" && kind !== "context_stats";
}

function researchCount(text: string): number | undefined {
    const found = /^\s*Found\s+(\d+)\b/i.exec(text);
    if (found) return Number(found[1]);
    return /^\s*No\b/i.test(text) ? 0 : undefined;
}

/** What one helper's lookups found, by noun, and how many lookups it made. */
interface HelperTally {
    found: Map<string, number>;
    /** Lookups made, and how many of them reported a count. */
    lookups: number;
    counted: number;
}

interface Draft {
    step: TurnStep;
    seq: number;
    toolName?: string;
    /** The server reports this step's running/complete state (`op` is only sent by those). */
    tracked: boolean;
    streaming: boolean;
    failed: boolean;
    /** Set on a helper's step: the id its lookups group by (`helperId ?? researchId`). */
    helperId?: string;
    tally?: HelperTally;
    /** The plan group this helper reads (its parts' `planGroup` tag) and its last lookups. */
    group?: ActivityPlanGroup;
    lookups?: LaneLookup[];
    /** A plan group's own progress line: the plan block's lane draws it, so the step list does not. */
    groupLine?: boolean;
}

const PROGRESS_TOOL = "rfi_progress";
/** How many of a helper's last lookups its lane keeps. */
const LANE_LOOKUPS = 2;

const DEFAULT_HELPER_NAME = "MiniMarv";

/**
 * A helper's step says what it found: the one noun every lookup counted, summed ("51 sheets"),
 * else how many lookups it made. A row never shows part of its lookups' counts.
 */
function tallyResult(tally: HelperTally): Pick<TurnStep, "resultCount" | "resultNoun"> {
    if (tally.found.size === 1 && tally.counted === tally.lookups) {
        const [[noun, count]] = [...tally.found];
        return { resultCount: count, resultNoun: noun };
    }
    return tally.lookups > 0 ? { resultCount: tally.lookups, resultNoun: "lookups" } : {};
}

function addFound(tally: HelperTally, noun: string, count: number): void {
    tally.found.set(noun, (tally.found.get(noun) ?? 0) + count);
    tally.counted += 1;
}

/** The step for a helper's first lookup: later lookups of the same helper add to it. */
function newHelperDraft(
    drafts: Draft[],
    seq: number,
    helperId: string,
    name: string | undefined,
    op: string | undefined,
    tags: Pick<ActivityPartPayload, "planGroup" | "helperTask">,
): Draft {
    const draft: Draft = {
        seq,
        tracked: false,
        streaming: false,
        failed: false,
        helperId,
        group: validGroup(tags.planGroup),
        lookups: [],
        tally: { found: new Map(), lookups: 0, counted: 0 },
        step: {
            id: `step-${seq}`,
            op: typeof op === "string" && STEP_OPS.has(op) ? (op as StepOp) : "check",
            label: "Searching the documents",
            status: "done",
            helper: name || DEFAULT_HELPER_NAME,
            ...(typeof tags.helperTask === "string" && tags.helperTask
                ? { task: tags.helperTask }
                : {}),
        },
    };
    drafts.push(draft);
    return draft;
}

/** A finished `plan` step: "Listing the sections", for the 136 specification sections it listed. */
function plannedLabel(payload: ActivityPartPayload): string | null {
    return typeof payload.resultCount === "number" && typeof payload.resultNoun === "string"
        ? `Listing the ${shortNoun(payload.resultNoun)}`
        : null;
}

/** A helper's plan group tag when it names a plan and a group number; anything else is no group. */
function validGroup(tag: ActivityPlanGroup | undefined): ActivityPlanGroup | undefined {
    return tag && typeof tag.planId === "string" && typeof tag.index === "number" ? tag : undefined;
}

/**
 * Builds the turn from its parts. `resolve` turns citation markers in a label into readable
 * names. Only a live turn has a running step; a stopped or dropped turn marks the step that
 * was running when it ended.
 */
export function buildTurnModel(
    parts: ApiMessagePart[],
    phase: TurnPhase,
    resolve: (text: string) => string = (text) => text,
): TurnModel {
    const drafts: Draft[] = [];
    let hasText = false;
    let waiting = false;
    let lastFlowSeq = -1;
    let lastFlowKind = "";
    let lastQuestionSeq = -1;

    for (const candidate of parts) {
        const part = parseApiMessagePart(candidate);
        if (!part) continue;
        if (isFlowPart(part.kind)) {
            lastFlowSeq = part.seq;
            lastFlowKind = part.kind;
        }
        if (part.kind === "text") {
            if (part.payload.text) hasText = true;
            continue;
        }
        if (part.kind === "question") {
            if (part.payload.status === "pending") waiting = true;
            lastQuestionSeq = part.seq;
            continue;
        }
        if (part.kind !== "activity") continue;
        const payload = part.payload;
        const helperId = helperIdOf(payload);
        if (helperId) {
            // A helper's lookups are one step, whatever the number of them. Its thinking and results
            // keep that step the one in flight; its counts add up on it.
            const owner = drafts.find((d) => d.helperId === helperId);
            if (owner) owner.seq = part.seq;
            if (payload.kind === "result") {
                const count = researchCount(payload.text);
                if (owner?.tally && count !== undefined) {
                    addFound(owner.tally, "sections", count);
                    Object.assign(owner.step, tallyResult(owner.tally));
                }
                continue;
            }
            if (payload.kind !== "tool") continue;
            const lookup =
                owner ??
                newHelperDraft(drafts, part.seq, helperId, payload.helperName, payload.op, payload);
            lookup.lookups!.push({
                label: payload.label ?? payload.text,
                item: lookupItem(payload.label ?? payload.text),
            });
            lookup.tally!.lookups += 1;
            if (typeof payload.resultCount === "number" && typeof payload.resultNoun === "string") {
                addFound(lookup.tally!, payload.resultNoun, payload.resultCount);
            }
            Object.assign(lookup.step, tallyResult(lookup.tally!));
            continue;
        }
        if (payload.kind === "result") continue;
        if (payload.kind !== "tool") continue;
        const progress = payload.toolName === PROGRESS_TOOL ? parsePlan(payload.plan) : null;
        const raw = payload.label ?? payload.toolName ?? payload.text;
        const planned = payload.toolName === "plan" ? plannedLabel(payload) : null;
        const label = planned ?? resolve(raw);
        const refs = [...serverRefs(payload.labelRefs), ...tokenRefs(raw ?? "", resolve)];
        const serverOp =
            typeof payload.op === "string" && STEP_OPS.has(payload.op) ? payload.op : null;
        drafts.push({
            seq: part.seq,
            toolName: payload.toolName,
            tracked: serverOp !== null,
            streaming: part.status === "streaming",
            failed: payload.failed === true,
            groupLine:
                payload.toolName === PROGRESS_TOOL && validGroup(payload.planGroup) !== undefined,
            step: {
                id: `step-${part.seq}`,
                op: (serverOp as StepOp | null) ?? opForTool(payload.toolName, label),
                label,
                status: "done",
                ...(payload.toolName === NARRATE_TOOL ? { kind: "narration" as const } : {}),
                ...(progress ? { kind: "plan" as const, plan: progress } : {}),
                resultCount:
                    typeof payload.resultCount === "number" ? payload.resultCount : undefined,
                resultNoun:
                    typeof payload.resultNoun === "string"
                        ? planned
                            ? shortNoun(payload.resultNoun)
                            : payload.resultNoun
                        : undefined,
                ...(payload.failed === true && typeof payload.error === "string"
                    ? { error: payload.error }
                    : {}),
                ...(refs.length > 0 ? { refs } : {}),
            },
        });
    }

    // A Deep research turn draws its plan as one block and each group's MiniMarv as a lane in it, so neither the
    // group's progress line nor its helper is a step of its own. Without a plan (a record from before plans were
    // tagged) they stay ordinary rows.
    const plan = drafts.find((draft) => draft.step.kind === "plan")?.step.plan ?? null;
    const lanes: TurnLane[] = [];
    if (plan) {
        for (let i = drafts.length - 1; i >= 0; i--) {
            const draft = drafts[i];
            if (draft.groupLine) drafts.splice(i, 1);
            else if (draft.group && draft.helperId) {
                drafts.splice(i, 1);
                lanes.unshift(laneOf(draft, draft.group, plan));
            }
        }
        lanes.sort((a, b) => a.group - b.group);
    }

    // A question means the steps before it finished: once it is asked, none of them runs or
    // ticks "Still …", whether or not the server closed it.
    for (const draft of drafts) if (draft.seq < lastQuestionSeq) draft.streaming = false;

    // The step in flight: the server's own running step, else (an older server, or a turn
    // reloaded after it stopped) the last step when nothing was drawn after it.
    const live = phase === "live";
    // Narration is commentary between steps: it is never the step in flight.
    const isWork = (draft: Draft) => draft.step.kind !== "narration" && draft.step.kind !== "plan";
    const last = [...drafts].reverse().find(isWork);
    const lastInFlight =
        last !== undefined &&
        (live && last.tracked ? last.streaming : last.streaming || last.seq === lastFlowSeq);
    for (const draft of drafts.filter(isWork)) {
        if (draft.failed) draft.step.status = "failed";
        // A delegation still researching while its searches run: in flight, but not the one
        // step the header names.
        else if (live && draft.tracked && draft.streaming) draft.step.status = "running";
    }
    let current: TurnStep | null = null;
    if (last && lastInFlight && !last.failed) {
        if (live) {
            last.step.status = "running";
            current = last.step;
        } else if (phase === "cancelled") {
            last.step.status = "stopped";
        } else if (phase === "error") {
            last.step.status = "unfinished";
        }
    }
    // A step still running under finished ones (an RFI check while its own lines finish, after an
    // answer resumed the turn): the header names it rather than "Thinking". A research
    // delegation is left out: its searches name what it is doing.
    if (live && current === null) {
        const running = [...drafts]
            .reverse()
            .find(
                (draft) =>
                    draft.step.status === "running" && draft.toolName !== "task" && isWork(draft),
            );
        current = running?.step ?? null;
    }
    return {
        steps: drafts.map((d) => d.step),
        plan,
        lanes,
        current,
        hasText,
        writing: live && lastFlowKind === "text",
        waiting: live && waiting,
    };
}

/** The lane of a plan group's helper: the group it reads, its state in the plan, and its last lookups. */
function laneOf(draft: Draft, tag: ActivityPlanGroup, plan: PlanModel): TurnLane {
    const entry = plan.groups.find((group) => group.index === tag.index);
    return {
        id: draft.step.id,
        helperId: draft.helperId ?? "",
        name: draft.step.helper ?? DEFAULT_HELPER_NAME,
        ...(draft.step.task ? { task: draft.step.task } : {}),
        planId: tag.planId,
        group: tag.index,
        label: entry?.label || tag.label || "",
        size: entry?.size ?? tag.size ?? 0,
        worker: entry?.worker ?? tag.worker,
        status: entry?.status ?? "reading",
        lookups: (draft.lookups ?? []).slice(-LANE_LOOKUPS),
        opened: draft.lookups ?? [],
    };
}

/** One stretch of a turn between questions: its steps and the phase its header shows. */
export interface TurnSegment {
    turn: TurnModel;
    phase: TurnPhase;
}

/**
 * The turn split at each question Marvin asked, so the steps read in the order they happened:
 * steps before the question, then (after the question and the user's reply) the steps after
 * it. Every segment but the live one reads as finished (`complete`); the live one is the last,
 * or, while a question waits for its answer, the one that asked it. A turn with no question is
 * one segment, the same model {@link buildTurnModel} gives.
 */
export function buildTurnSegments(
    parts: ApiMessagePart[],
    phase: TurnPhase,
    resolve: (text: string) => string = (text) => text,
): TurnSegment[] {
    const questions = parts
        .map((candidate) => parseApiMessagePart(candidate))
        .filter((part) => part !== null && part.kind === "question")
        .sort((a, b) => (a?.seq ?? 0) - (b?.seq ?? 0));
    if (questions.length === 0) return [{ turn: buildTurnModel(parts, phase, resolve), phase }];

    const seqs = questions.map((part) => part?.seq ?? 0);
    const lastQuestion = questions[questions.length - 1];
    const waitingOnLast =
        phase === "live" &&
        lastQuestion?.kind === "question" &&
        lastQuestion.payload.status === "pending";
    // While a question waits there is nothing after it yet: its own segment is the live one.
    const count = waitingOnLast ? seqs.length : seqs.length + 1;
    const segments: TurnSegment[] = [];
    for (let i = 0; i < count; i++) {
        const from = i === 0 ? -Infinity : seqs[i - 1];
        const to = i < seqs.length ? seqs[i] : Infinity;
        // The question that closes a segment stays in it, so its steps count as finished.
        const own = parts.filter((part) => part.seq > from && part.seq <= to);
        const segmentPhase: TurnPhase = i === count - 1 ? phase : "complete";
        segments.push({ turn: buildTurnModel(own, segmentPhase, resolve), phase: segmentPhase });
    }
    return segments;
}

const DRAWING_NOUNS = new Set(["drawings", "sheets", "drawing", "sheet"]);

/**
 * What the finished steps covered, for the folded header ("Looked at the drawings and
 * checked 12 RFIs"). Counts come from the steps' own results; without them the line names
 * the kinds of work. Null when the steps only thought or wrote.
 */
export function turnSummary(steps: TurnStep[]): string | null {
    const work = steps.filter(isCountedStep);
    const looks = work.filter((s) => s.op === "look");
    const checks = work.filter((s) => s.op === "check");
    const segments: string[] = [];
    if (looks.length > 0) {
        const drawings = looks
            .filter((s) => s.resultNoun && DRAWING_NOUNS.has(s.resultNoun))
            .reduce((n, s) => n + (s.resultCount ?? 0), 0);
        segments.push(
            drawings > 0
                ? `looked at ${drawings} ${drawings === 1 ? "drawing" : "drawings"}`
                : "looked at the drawings",
        );
    }
    if (checks.length > 0) {
        const byNoun = new Map<string, number>();
        for (const step of checks) {
            if (!step.resultNoun || !step.resultCount || step.resultNoun === "results") continue;
            byNoun.set(step.resultNoun, (byNoun.get(step.resultNoun) ?? 0) + step.resultCount);
        }
        const counted = [...byNoun].map(([noun, count]) => resultText(count, noun) as string);
        segments.push(
            counted.length > 0 ? `checked ${counted.join(" and ")}` : "checked the records",
        );
    }
    if (segments.length === 0) return null;
    const text = segments.join(" and ");
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "3 steps", "1 step". */
export function stepCountText(count: number): string {
    return `${count} ${count === 1 ? "step" : "steps"}`;
}

/** "0:05", "12:40". */
export function clockText(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
